import { useEffect, useState } from 'react'
import { Alert, Button, Form, Message } from '@arco-design/web-react'
import { getDeploymentSettingsApi, testDeploymentConnectionApi, type DeploymentSettingsResponse } from '@/api/deployment'
import { Pill } from '@/utils/render'
import InstantSwitch from './InstantSwitch'
import SensitiveInput from './SensitiveInput'
import SettingSectionCard from './SettingSectionCard'
import type { OptionsAPI } from './useOptions'

const KEYS = ['model_deployment.ionet.enabled', 'model_deployment.ionet.api_key']

function deploymentStatusPill(settings: DeploymentSettingsResponse | null, enabled: boolean) {
  if (!enabled) return <Pill variant="neutral">未启用</Pill>
  if (!settings) return <Pill variant="warning">加载中</Pill>
  if (!settings.configured) return <Pill variant="warning">未配置密钥</Pill>
  if (settings.can_connect) return <Pill variant="success">已连接</Pill>
  return <Pill variant="danger">不可连接</Pill>
}

export default function ModelDeploymentSetting({ options }: { options: OptionsAPI }) {
  const { get, getBool, patch } = options
  const [settings, setSettings] = useState<DeploymentSettingsResponse | null>(null)
  const [testing, setTesting] = useState(false)
  const enabled = getBool('model_deployment.ionet.enabled')
  const draftKey = get('model_deployment.ionet.api_key').trim()

  const loadSettings = async () => {
    try {
      const data = await getDeploymentSettingsApi()
      setSettings(data)
    } catch {
      setSettings(null)
    }
  }

  useEffect(() => {
    loadSettings()
  }, [options.dataRevision, enabled])

  const canTest = enabled && (draftKey.length > 0 || settings?.configured)

  const testConnection = async () => {
    if (!canTest) {
      Message.warning('请先启用并填写 API Key，或保存已配置的密钥后再测试')
      return
    }
    setTesting(true)
    try {
      await testDeploymentConnectionApi(draftKey ? { api_key: draftKey } : {})
      Message.success('连接测试成功')
      await loadSettings()
    } catch (err) {
      Message.error(err instanceof Error ? err.message : '连接测试失败')
      await loadSettings()
    } finally {
      setTesting(false)
    }
  }

  return (
    <SettingSectionCard
      title="io.net 模型部署"
      subtitle="后端仅读取 model_deployment.ionet.enabled 与 model_deployment.ionet.api_key 两个 option；部署列表在「模型部署」页管理"
      keys={KEYS}
      options={options}
    >
      <Alert
        type="info"
        className="mb-4"
        content={
          <div className="flex flex-wrap items-center gap-2">
            <span>当前运行时状态：</span>
            {deploymentStatusPill(settings, enabled)}
            {settings?.provider ? <span className="text-xs text-gray-500">provider: {settings.provider}</span> : null}
          </div>
        }
      />
      <Form layout="vertical">
        <Form.Item label="启用 io.net 部署">
          <InstantSwitch options={options} optionKey="model_deployment.ionet.enabled" />
        </Form.Item>
        <Form.Item label="io.net API Key" extra="GET 不返回已保存值；留空保存不覆盖">
          <SensitiveInput
            value={get('model_deployment.ionet.api_key')}
            onChange={(v: string) => patch({ 'model_deployment.ionet.api_key': v })}
          />
        </Form.Item>
        <Button type="outline" loading={testing} disabled={!canTest} onClick={testConnection}>
          测试连接
        </Button>
        <div className="text-xs text-gray-500 mt-2">
          保存密钥后建议重新测试连接；状态来自 GET /api/deployments/settings。
        </div>
      </Form>
    </SettingSectionCard>
  )
}
